import React, { useState } from "react";
import { useFormikContext } from "formik";
import { Button, Flex } from "@jsq/ds/core";
import { FormTextInput } from "@jsq/ds/forms";
import { parseFullName } from "./utils";

interface NameFormFieldsProps {
  onNameChange?: (nameParts: {
    firstName: string;
    middleName: string;
    lastName: string;
  }) => void;
}

export const NameFormFields: React.FC<NameFormFieldsProps> = ({ onNameChange }) => {
  const [showNameFields, setShowNameFields] = useState(false);
  const formContext = useFormikContext<{
    firstName: string;
    middleName: string;
    lastName: string;
  }>();

  const handleShowNameFields = () => {
    const values = formContext.values;
    if (!showNameFields) {
      // Switching to split mode - parse the full name
      const {
        firstName: parsedFirst,
        middleName: parsedMiddle,
        lastName: parsedLast,
      } = parseFullName(values.lastName || "");
      void formContext.setFieldValue("firstName", parsedFirst);
      void formContext.setFieldValue("middleName", parsedMiddle);
      void formContext.setFieldValue("lastName", parsedLast);
    } else {
      // Switching to single field mode - combine the names
      const fullName = [values.firstName, values.middleName, values.lastName]
        .filter(Boolean)
        .join(" ");
      void formContext.setFieldValue("firstName", "");
      void formContext.setFieldValue("middleName", "");
      void formContext.setFieldValue("lastName", fullName);
    }
    setShowNameFields(!showNameFields);
  };

  const handleFullNameChange = (value: string) => {
    // Parse the full name into components with distinct variable names
    const {
      firstName: parsedFirst,
      middleName: parsedMiddle,
      lastName: parsedLast,
    } = parseFullName(value);

    // Store the parsed values in their respective fields
    void formContext.setFieldValue("firstName", parsedFirst);
    void formContext.setFieldValue("middleName", parsedMiddle);
    void formContext.setFieldValue("lastName", parsedLast);

    if (onNameChange) {
      onNameChange({ firstName: parsedFirst, middleName: parsedMiddle, lastName: parsedLast });
    }
  };

  return (
    <>
      {showNameFields ? (
        <>
          <Flex justifyContent="space-between" gap={12}>
            <div>
              <FormTextInput name="firstName" label="First name" isFullWidth />
            </div>
            <div>
              <FormTextInput name="middleName" label="Middle name" isFullWidth />
            </div>
            <div>
              <FormTextInput name="lastName" label="Last name" isFullWidth />
            </div>
          </Flex>
          <Button type="button" onClick={handleShowNameFields} displayAsLink>
            Hide first/middle/last names
          </Button>
        </>
      ) : (
        <>
          <FormTextInput
            name="lastName"
            label="Name"
            isFullWidth
            onChange={(e) => handleFullNameChange(e.target.value)}
          />
          <Button type="button" onClick={handleShowNameFields} displayAsLink>
            Show first/middle/last names
          </Button>
        </>
      )}
    </>
  );
};
